/**
 * WorkflowMessagesContext — Messages slice of the Athena workflow.
 *
 * Owns the conversation `messages` array and the three actions that
 * mutate it (ADD_MESSAGE / UPDATE_MESSAGE / CLEAR_MESSAGES).
 *
 * `messageCounterRef` is local to this provider — it's only used by
 * `addMessage` to mint stable unique IDs when callers don't supply one.
 *
 * Hook surface mirrors the existing flat-object pattern (state spread +
 * action-creator functions, NOT { state, dispatch }).
 *
 * Refs:
 * - Modularity Epic 4 plan §4.2 (revised after Story 4.3 audit)
 * - docs/ATHENA_WORKFLOW_CONTEXT_SLICES.md §1 (Messages row)
 */
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useReducer,
  useRef,
} from 'react';

const WorkflowMessagesContext = createContext(null);

export const MESSAGES_ACTIONS = {
  ADD_MESSAGE: 'ADD_MESSAGE',
  UPDATE_MESSAGE: 'UPDATE_MESSAGE',
  CLEAR_MESSAGES: 'CLEAR_MESSAGES',
};

export const initialMessagesState = {
  messages: [],
};

export function messagesReducer(state, action) {
  switch (action.type) {
    case MESSAGES_ACTIONS.ADD_MESSAGE:
      return {
        ...state,
        messages: [...state.messages, action.payload],
      };

    case MESSAGES_ACTIONS.UPDATE_MESSAGE:
      return {
        ...state,
        messages: state.messages.map((msg) =>
          msg.id === action.payload.id ? { ...msg, ...action.payload.updates } : msg
        ),
      };

    case MESSAGES_ACTIONS.CLEAR_MESSAGES:
      return {
        ...state,
        messages: [],
      };

    default:
      return state;
  }
}

export function WorkflowMessagesProvider({ children }) {
  const [state, dispatch] = useReducer(messagesReducer, initialMessagesState);

  // Counter for unique IDs when the caller doesn't supply one.
  // Moved verbatim from WorkflowMachineContext.jsx (the previous home of
  // this ref + the addMessage action-creator).
  const messageCounterRef = useRef(0);

  const addMessage = useCallback((message) => {
    messageCounterRef.current += 1;
    const messageWithId = {
      ...message,
      id: message.id || `msg_${Date.now()}_${messageCounterRef.current}`,
      timestamp: message.timestamp || new Date().toISOString(),
    };
    dispatch({ type: MESSAGES_ACTIONS.ADD_MESSAGE, payload: messageWithId });
    return messageWithId;
  }, []);

  const updateMessage = useCallback((id, updates) => {
    dispatch({ type: MESSAGES_ACTIONS.UPDATE_MESSAGE, payload: { id, updates } });
  }, []);

  const clearMessages = useCallback(() => {
    dispatch({ type: MESSAGES_ACTIONS.CLEAR_MESSAGES });
  }, []);

  const contextValue = useMemo(() => ({
    // State
    ...state,

    // Action-creators
    addMessage,
    updateMessage,
    clearMessages,
  }), [state, addMessage, updateMessage, clearMessages]);

  return (
    <WorkflowMessagesContext.Provider value={contextValue}>
      {children}
    </WorkflowMessagesContext.Provider>
  );
}

export function useWorkflowMessages() {
  const context = useContext(WorkflowMessagesContext);

  if (!context) {
    throw new Error('useWorkflowMessages must be used within a WorkflowMessagesProvider');
  }

  return context;
}
